import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import api, { money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, CaretRight, Warning, Plus, DownloadSimple, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { EmailReportDialog } from "@/components/EmailReportDialog";

const GROUP_COLORS = { "Hard Costs": "#0A0A0A", "Soft Costs": "#B45309", "Unclassified": "#D1D5DB" };
const SUB_COLORS = ["#0A0A0A", "#B45309", "#2563EB", "#059669", "#7C3AED", "#DB2777", "#D97706", "#0891B2", "#4B5563", "#65A30D"];

// data: [{group, budget, actual}]  categories: [{category, group, budget, actual}]  projectId: scope (optional)
export function HardSoftBreakdown({ data = [], categories = [], projectId = null, testId = "hard-soft-breakdown" }) {
  const [metric, setMetric] = useState("budget");
  const [drill, setDrill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailQuoted, setDetailQuoted] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const navigate = useNavigate();

  const goToItem = (it) => {
    setDetail(null);
    navigate(`/projects/${it.project_id}?item=${it.id}&view=receipts`);
  };

  const downloadDetail = async (fmt) => {
    try {
      const params = { name: detail };
      if (projectId) params.project_id = projectId;
      const res = await api.get(`/categories/line-items/export.${fmt}`, { params, responseType: "blob" });
      const mime = fmt === "csv" ? "text/csv"
        : fmt === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${detail.replace(/\s+/g, "_")}_cost_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download report"); }
  };

  const canDrill = (group) => categories.some((c) => c.group === group && ((c.budget || 0) > 0 || (c.actual || 0) > 0));

  const openDetail = async (name) => {
    setDetail(name);
    setDetailItems([]);
    setDetailQuoted(0);
    setDetailLoading(true);
    try {
      const params = { name };
      if (projectId) params.project_id = projectId;
      const { data: res } = await api.get("/categories/line-items", { params });
      setDetailItems(res.items || []);
      setDetailQuoted(res.quoted || 0);
    } catch { setDetailItems([]); }
    finally { setDetailLoading(false); }
  };

  let rows, title, isSub = false;
  if (drill) {
    isSub = true;
    rows = categories
      .filter((c) => c.group === drill && ((c.budget || 0) > 0 || (c.actual || 0) > 0))
      .map((c, i) => ({ name: c.category, budget: c.budget || 0, actual: c.actual || 0, quoted: c.quoted || 0, color: SUB_COLORS[i % SUB_COLORS.length] }));
    title = drill;
  } else {
    rows = data
      .filter((d) => (d.budget || 0) > 0 || (d.actual || 0) > 0)
      .map((d) => ({ name: d.group, budget: d.budget || 0, actual: d.actual || 0, quoted: d.quoted || 0, color: GROUP_COLORS[d.group] || "#D1D5DB" }));
    title = "Hard vs Soft Costs";
  }

  const total = rows.reduce((s, r) => s + (r[metric] || 0), 0);
  const pie = rows.map((r) => ({ name: r.name, value: r[metric] || 0, color: r.color }));
  const empty = total === 0;
  const detailBudget = detailItems.reduce((s, i) => s + (i.budget_amount || 0), 0);
  const detailActual = detailItems.reduce((s, i) => s + (i.actual_amount || 0), 0);

  return (
    <div className="border border-border" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-secondary">
        <div className="flex items-center gap-2 min-w-0">
          {isSub && (
            <button onClick={() => setDrill(null)} data-testid="breakdown-back" className="text-muted-foreground hover:text-primary shrink-0" aria-label="Back">
              <ArrowLeft size={16} weight="bold" />
            </button>
          )}
          <span className="font-heading font-bold truncate">{title}</span>
          {isSub && <span className="text-xs text-muted-foreground">click a sub-category for line items</span>}
        </div>
        <div className="flex border border-border shrink-0" data-testid="breakdown-metric-toggle">
          {["budget", "actual"].map((m) => (
            <button key={m} onClick={() => setMetric(m)} data-testid={`breakdown-metric-${m}`}
              className={`px-3 py-1 text-xs capitalize transition-colors ${metric === m ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-primary"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>
      {empty ? (
        <div className="p-8 text-sm text-muted-foreground">No {metric} data yet. Add cost items using the Hard/Soft categories to see the split.</div>
      ) : (
        <div className="grid sm:grid-cols-2 items-center">
          <div className="relative p-4" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2} stroke="none">
                  {pie.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="label-caps">Total {metric}</span>
              <span className="font-mono-fig text-xl font-bold tabular-nums">{money(total)}</span>
            </div>
          </div>
          <div className="hidden sm:block p-4 space-y-3 border-t sm:border-t-0 sm:border-l border-border">
            {rows.map((r) => {
              const pct = total > 0 ? ((r[metric] || 0) / total) * 100 : 0;
              const over = (r.actual || 0) > (r.budget || 0) && (r.budget || 0) > 0;
              const quoteOver = (r.quoted || 0) > (r.budget || 0) && (r.budget || 0) > 0;
              const clickable = isSub ? true : canDrill(r.name);
              const slug = r.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
              const Body = (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-3 h-3 inline-block shrink-0" style={{ background: r.color }} />
                      <span className="truncate">{r.name}</span>
                      {quoteOver && <Warning size={13} weight="fill" className="text-destructive shrink-0" title="Accepted quotes exceed budget" />}
                      {clickable && <CaretRight size={12} className="text-muted-foreground shrink-0" />}
                    </span>
                    <span className="font-mono-fig tabular-nums font-medium shrink-0">{pct.toFixed(0)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-1 pl-5 gap-2">
                    <span className="whitespace-nowrap">Budget <span className="font-mono-fig text-foreground">{money(r.budget)}</span></span>
                    <span className="whitespace-nowrap">Quoted <span className={`font-mono-fig ${quoteOver ? "text-destructive font-semibold" : "text-amber-600"}`}>{money(r.quoted || 0)}</span></span>
                    <span className="whitespace-nowrap">Actual <span className={`font-mono-fig ${over ? "text-destructive" : "text-foreground"}`}>{money(r.actual)}</span></span>
                  </div>
                </>
              );
              return clickable ? (
                <button key={r.name} onClick={() => (isSub ? openDetail(r.name) : setDrill(r.name))}
                  data-testid={isSub ? `breakdown-detail-${slug}` : `breakdown-drill-${slug}`}
                  className="w-full text-left hover:bg-secondary -mx-2 px-2 py-1 transition-colors">
                  {Body}
                </button>
              ) : (
                <div key={r.name} data-testid={`breakdown-${slug}`}>{Body}</div>
              );
            })}
          </div>

          <div className="sm:hidden p-4 border-t border-border" data-testid="breakdown-mobile-select">
            <p className="label-caps mb-2">Cost report by category</p>
            <Select value="" onValueChange={(v) => openDetail(v)}>
              <SelectTrigger className="rounded-none w-full" data-testid="breakdown-mobile-select-trigger">
                <SelectValue placeholder="Select a category to view its report…" />
              </SelectTrigger>
              <SelectContent>
                {[...categories]
                  .filter((c) => (c.budget || 0) > 0 || (c.actual || 0) > 0)
                  .sort((a, b) => (a.group || "").localeCompare(b.group || "") || a.category.localeCompare(b.category))
                  .map((c) => (
                    <SelectItem key={c.category} value={c.category}
                      data-testid={`breakdown-mobile-option-${c.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      {c.category} · {money(c[metric] || 0)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <DialogTitle className="font-heading">{detail} — line items</DialogTitle>
              {detailItems.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 sm:mr-8">
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="breakdown-email"
                    onClick={() => setEmailOpen(true)}>
                    <PaperPlaneTilt size={14} className="mr-1" /> Email
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="breakdown-export-pdf"
                    onClick={() => downloadDetail("pdf")}>
                    <FilePdf size={14} className="mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="breakdown-export-csv"
                    onClick={() => downloadDetail("csv")}>
                    <DownloadSimple size={14} className="mr-1" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none h-8" data-testid="breakdown-export-xlsx"
                    onClick={() => downloadDetail("xlsx")}>
                    <DownloadSimple size={14} className="mr-1" /> Excel
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Click a row to open its project & attached invoices.</p>
          {detailLoading ? (
            <p className="text-sm text-muted-foreground animate-pulse py-6">Loading…</p>
          ) : detailItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">No line items in this category{projectId ? " for this project" : ""}.</p>
          ) : (
            <div data-testid="breakdown-detail-table" className="overflow-x-auto -mx-6 px-6">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {!projectId && <TableHead>Project</TableHead>}
                    <TableHead>Description</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailItems.map((it) => {
                    const over = (it.actual_amount || 0) > (it.budget_amount || 0) && (it.budget_amount || 0) > 0;
                    return (
                      <TableRow key={it.id} onClick={() => goToItem(it)} data-testid={`breakdown-item-${it.id}`}
                        className={`cursor-pointer ${over ? "bg-destructive/5 hover:bg-destructive/10" : "hover:bg-secondary"}`}>
                        {!projectId && <TableCell className="text-sm">{it.project_name}</TableCell>}
                        <TableCell className="text-sm max-w-[220px] truncate">
                          <span className="flex items-center gap-1.5">
                            {over && <Warning size={13} weight="fill" className="text-destructive shrink-0" data-testid={`breakdown-over-${it.id}`} />}
                            <span className="truncate">{it.description}</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{it.vendor_name || "—"}</TableCell>
                        <TableCell className="text-right font-mono-fig tabular-nums">{money(it.budget_amount)}</TableCell>
                        <TableCell className={`text-right font-mono-fig tabular-nums ${over ? "text-destructive font-medium" : ""}`}>{money(it.actual_amount)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <button onClick={(e) => { e.stopPropagation(); goToItem(it); }} data-testid={`breakdown-add-invoice-${it.id}`}
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 border transition-colors ${over ? "border-destructive text-destructive hover:bg-destructive hover:text-white" : "border-border text-muted-foreground hover:text-primary hover:border-primary"}`}>
                            <Plus size={12} weight="bold" /> Invoice
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="hover:bg-transparent border-t-2 border-border font-semibold">
                    <TableCell colSpan={projectId ? 2 : 3}>Total</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(detailBudget)}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(detailActual)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
          {detailItems.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm border-t border-border pt-3" data-testid="breakdown-detail-summary">
              <span>Budget <span className="font-mono-fig font-semibold">{money(detailBudget)}</span></span>
              <span>Quoted (accepted) <span className="font-mono-fig font-semibold text-amber-600" data-testid="breakdown-detail-quoted">{money(detailQuoted)}</span></span>
              <span>Actual <span className={`font-mono-fig font-semibold ${detailActual > detailBudget ? "text-destructive" : ""}`}>{money(detailActual)}</span></span>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailReportDialog open={emailOpen} onOpenChange={setEmailOpen} categoryName={detail} projectId={projectId} />
    </div>
  );
}
